import type { OilPhaseVolumeFactorBoCalculator } from "../../d";
import { solutionGasRsoCalculator } from "./solutionGasRsoCalculator";

export const oilPhaseVolumeFactorBoCalculator: OilPhaseVolumeFactorBoCalculator =
  params => {
    const {
      gasSpecificGravity,
      oilAPI,
      temperatureInRankine,
      inSituPressurePSIA,
      oilSpecificGravity,
      temperatureF,
      bubblePointPressurePSIA,
      solutionGasRsoInSitu,
      oilPhaseVolumeFactorBoSatOverride,
      gasOilRatio
    } = params;
    const { gasSGatSeparatorYgPsp } = solutionGasRsoCalculator(params);

    // D188
    const a1 = 0.0004677;
    // D189
    const a2 = 0.00001751;
    // D190
    const a3 = -1.811 * 10 ** -8;

    // D208: =0.01347*(D156**0.3873)*(J18**1.1715)*(10**((12.753*D7)/D15))
    const solutionGasPbRsb_gt_31 =
      0.01347 *
      gasSGatSeparatorYgPsp ** 0.3873 *
      bubblePointPressurePSIA ** 1.1715 *
      10 ** ((12.753 * oilAPI) / temperatureInRankine);
    // D210: =0.10084*(D156**0.2556)*(J18**0.9868)*(10**((7.4576*D7)/D15))
    const solutionGasPbRsb_22_31 =
      0.10084 *
      gasSGatSeparatorYgPsp ** 0.2556 *
      bubblePointPressurePSIA ** 0.9868 *
      10 ** ((7.4576 * oilAPI) / temperatureInRankine);
    // D212: =((D156*(J18**1.2057))/56.434)*(10**(10.9267*D17/D15))
    const solutionGasPbRsb_10_22 =
      ((gasSGatSeparatorYgPsp * bubblePointPressurePSIA ** 1.2057) / 56.434) *
      10 ** ((10.9267 * oilSpecificGravity) / temperatureInRankine);
    // D214: =D8*((J18/10.7025)*10**(0.0169*D17-0.00156*D4))**1.1128
    const solutionGasPbRsb_lt_10 =
      gasSpecificGravity *
      ((bubblePointPressurePSIA / 10.7025) *
        10 ** (0.0169 * oilSpecificGravity - 0.00156 * temperatureF)) **
        1.1128;

    let solutionGasPbRsb;
    // D216: =IF(D7<=10,D214,IF(AND(D7>10,D7<=22.3),D212,IF(AND(D7<=31.1,D7>22.3),D210,D208)))
    if (oilAPI > 31.1) {
      solutionGasPbRsb = solutionGasPbRsb_gt_31;
    } else if (oilAPI > 22.3 && oilAPI <= 31.1) {
      solutionGasPbRsb = solutionGasPbRsb_22_31;
    } else if (oilAPI > 10 && oilAPI <= 22.3) {
      solutionGasPbRsb = solutionGasPbRsb_10_22;
    } else {
      solutionGasPbRsb = solutionGasPbRsb_lt_10;
    }
    solutionGasPbRsb =
      solutionGasPbRsb > gasOilRatio ? gasOilRatio : solutionGasPbRsb;
    // NOTE: oilPhaseVolumeFactorBoSatOverride WILL BE PROVIDED WHEN CALCULATING oilFormationVolumeFactorUnSatBo
    // D192: =1+D188*J12+D189*(D4-60)*(D7/D8)+D190*J12*(D4-60)*(D7/D8)
    const oilFormationVolumeFactorSatBo =
      oilPhaseVolumeFactorBoSatOverride ??
      1 +
        a1 * solutionGasRsoInSitu +
        a2 * (temperatureF - 60) * (oilAPI / gasSpecificGravity) +
        a3 *
          solutionGasRsoInSitu *
          (temperatureF - 60) *
          (oilAPI / gasSpecificGravity);
    // D194: =(10**-6.1646*J21**1.8789*D7**0.3646*D4**0.1966)-(1-J18/D3)*10**(-8.98*J21**3.9392*D4**1.349)
    const oilCompressibilityUnSatCo_gt_31 =
      10 ** -6.1646 *
        oilFormationVolumeFactorSatBo ** 1.8789 *
        oilAPI ** 0.3646 *
        temperatureF ** 0.1966 -
      (1 - bubblePointPressurePSIA / inSituPressurePSIA) *
        10 **
          (-8.98 *
            oilFormationVolumeFactorSatBo ** 3.9392 *
            temperatureF ** 1.349);
    // D196: =(-705.288+2.2246*D216+26.0644*D4-2080.823*D156-9.6807*D7)/(D3*10**5)
    const oilCompressibilityUnSatCo_22_31 =
      (-705.288 +
        2.2246 * solutionGasPbRsb +
        26.0644 * temperatureF -
        2080.823 * gasSGatSeparatorYgPsp -
        9.6807 * oilAPI) /
      (inSituPressurePSIA * 10 ** 5);
    // D198: =(-2841.8+2.9646*D216+25.5439*D4-1230.5*D156+41.91*D7)/(D3*10**5)
    const oilCompressibilityUnSatCo_10_22 =
      (-2841.8 +
        2.9646 * solutionGasPbRsb +
        25.5439 * temperatureF -
        1230.5 * gasSGatSeparatorYgPsp +
        41.91 * oilAPI) /
      (inSituPressurePSIA * 10 ** 5);
    // D200: =(-889.6+3.1374*D216+20*D4-627.3*D156-81.4476*D7)/(D3*10**5)
    const oilCompressibilityUnSatCo_lt_10 =
      (-889.6 +
        3.1374 * solutionGasPbRsb +
        20 * temperatureF -
        627.3 * gasSGatSeparatorYgPsp -
        81.4476 * oilAPI) /
      (inSituPressurePSIA * 10 ** 5);

    let oilCompressibilityUnSatCo;
    // D202: =IF(D7<=10,D200,IF(AND(D7>10,D7<=22.3),D198,IF(AND(D7<=31.1,D7>22.3),D196,D194)))
    if (oilAPI > 31.1) {
      oilCompressibilityUnSatCo = oilCompressibilityUnSatCo_gt_31;
    } else if (oilAPI > 22.3 && oilAPI <= 31.1) {
      oilCompressibilityUnSatCo = oilCompressibilityUnSatCo_22_31;
    } else if (oilAPI > 10 && oilAPI <= 22.3) {
      oilCompressibilityUnSatCo = oilCompressibilityUnSatCo_10_22;
    } else {
      oilCompressibilityUnSatCo = oilCompressibilityUnSatCo_lt_10;
    }

    // D218: =1+D204*D216+D205*(D4-60)*(D7/D8)+D206*D216*(D4-60)*(D7/D8)
    const oilFormationVolumeFactoratPbBob =
      1 +
      a1 * solutionGasPbRsb +
      a2 * (temperatureF - 60) * (oilAPI / gasSpecificGravity) +
      a3 *
        solutionGasPbRsb *
        (temperatureF - 60) *
        (oilAPI / gasSpecificGravity);

    // D220: =D218*EXP(D202*(J18-D3))
    const oilFormationVolumeFactorUnSatBo =
      oilFormationVolumeFactoratPbBob *
      Math.exp(
        oilCompressibilityUnSatCo *
          (bubblePointPressurePSIA - inSituPressurePSIA)
      );

    return {
      bubblePointPressurePSIA,
      gasSpecificGravity,
      oilSpecificGravity,
      temperatureF,
      oilCompressibilityUnSatCo_lt_10,
      oilCompressibilityUnSatCo_10_22,
      oilCompressibilityUnSatCo_22_31,
      oilCompressibilityUnSatCo_gt_31,
      oilCompressibilityUnSatCo,
      oilFormationVolumeFactoratPbBob,
      solutionGasPbRsb,
      solutionGasPbRsb_gt_31,
      solutionGasPbRsb_22_31,
      solutionGasPbRsb_10_22,
      solutionGasPbRsb_lt_10,
      oilFormationVolumeFactorSatBo,
      oilFormationVolumeFactorUnSatBo
    };
  };
